import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faBuilding, faGlobe, faCheckCircle,
  faSpinner, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import Layout from '../Layout';
import logo from '../../assets/images/IMT_logo.png';
import CorporateGroupGraph from './CorporateGroupGraph';
import EntityDetailPanel from './panels/EntityDetailPanel';
import CreateEntityModal from './modals/CreateEntityModal';
import { fetchGroups, fetchGraphData } from '../../services/corporateStructureService';
import { useAuth } from '../../context/AuthContext';
import HamsterFloatingButton from '../HamsterFloatingButton';
import './SchernikauGroupDashboard.css';

const SchernikauGroupDashboard = () => {
  const { user } = useAuth();
  const isAdmin = !!user?.is_superuser;
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [graphData, setGraphData] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateEntityModal, setShowCreateEntityModal] = useState(false);

  const loadGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchGroups();
      const activeGroups = data.filter(g => g.is_active);
      setGroups(activeGroups);
      setSelectedGroupId(prev => {
        if (!prev && activeGroups.length > 0) return activeGroups[0].id;
        return prev;
      });
    } catch (err) {
      console.error('Failed to load groups:', err);
      setError('Failed to load corporate groups');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadGraphData = useCallback(async () => {
    if (!selectedGroupId) return;
    try {
      const data = await fetchGraphData(selectedGroupId);
      setGraphData(data);
    } catch (err) {
      console.error('Failed to load graph data:', err);
      setError('Failed to load group structure');
    }
  }, [selectedGroupId]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    if (selectedGroupId) {
      loadGraphData();
      setSelectedEntity(null);
    }
  }, [selectedGroupId, loadGraphData]);

  const handleNodeClick = useCallback((entityData) => {
    setSelectedEntity(entityData);
  }, []);

  const handleClosePanel = useCallback(() => {
    setSelectedEntity(null);
  }, []);

  const handleEntityCreated = useCallback(() => {
    setShowCreateEntityModal(false);
    loadGraphData();
  }, [loadGraphData]);

  const handleEntityUpdated = useCallback(() => {
    loadGraphData();
  }, [loadGraphData]);

  const selectedGroup = groups.find(g => g.id === selectedGroupId);

  const stats = graphData ? {
    entities: graphData.nodes.length,
    consolidation: graphData.consolidation_ids.length,
    countries: [...new Set(graphData.nodes.map(n => n.data.country))].length,
  } : { entities: 0, consolidation: 0, countries: 0 };

  return (
    <Layout>
      <div className="sg-dashboard">
        <div className="sg-header">
          <div className="sg-header-left">
            <img src={logo} alt="IMT" className="sg-header-logo-img" />
            <div>
              <h1 className="sg-title">Schernikau Group</h1>
              <p className="sg-subtitle">Corporate Structure Management</p>
            </div>
          </div>
          <div className="sg-header-actions">
            {isAdmin && (
              <button
                className="sg-btn sg-btn-secondary"
                onClick={() => setShowCreateEntityModal(true)}
                disabled={!selectedGroupId}
              >
                <FontAwesomeIcon icon={faPlus} />
                Add Entity
              </button>
            )}
          </div>
        </div>

        <div className="sg-stats-bar">
          <div className="sg-stat">
            <FontAwesomeIcon icon={faBuilding} className="sg-stat-icon" />
            <div className="sg-stat-content">
              <span className="sg-stat-value">{stats.entities}</span>
              <span className="sg-stat-label">Legal Entities</span>
            </div>
          </div>
          <div className="sg-stat">
            <FontAwesomeIcon icon={faCheckCircle} className="sg-stat-icon sg-stat-icon-green" />
            <div className="sg-stat-content">
              <span className="sg-stat-value">{stats.consolidation}</span>
              <span className="sg-stat-label">Consolidation Circle</span>
            </div>
          </div>
          <div className="sg-stat">
            <FontAwesomeIcon icon={faGlobe} className="sg-stat-icon sg-stat-icon-blue" />
            <div className="sg-stat-content">
              <span className="sg-stat-value">{stats.countries}</span>
              <span className="sg-stat-label">Countries</span>
            </div>
          </div>
        </div>

        {loading && (
          <div className="sg-loading">
            <FontAwesomeIcon icon={faSpinner} spin />
            <span>Loading corporate structure...</span>
          </div>
        )}

        {error && (
          <div className="sg-error">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>{error}</span>
            <button onClick={loadGroups} className="sg-btn sg-btn-secondary">Retry</button>
          </div>
        )}

        {!loading && !error && (
          <div className="sg-content">
            <div className="sg-graph-area">
              {graphData && (
                <CorporateGroupGraph
                  graphData={graphData}
                  onNodeClick={handleNodeClick}
                  selectedEntityId={selectedEntity?.id}
                />
              )}
            </div>
          </div>
        )}

        {selectedEntity && (
          <div className="edp-modal-overlay" onClick={handleClosePanel}>
            <div className="edp-modal-container" onClick={e => e.stopPropagation()}>
              <EntityDetailPanel
                entity={selectedEntity}
                groupId={selectedGroupId}
                existingEntities={graphData?.nodes?.map(n => n.data) || []}
                onClose={handleClosePanel}
                onEntityUpdated={handleEntityUpdated}
              />
            </div>
          </div>
        )}

        {showCreateEntityModal && selectedGroupId && (
          <CreateEntityModal
            groupId={selectedGroupId}
            groupName={selectedGroup?.name}
            existingEntities={graphData?.nodes?.map(n => n.data) || []}
            onClose={() => setShowCreateEntityModal(false)}
            onCreated={handleEntityCreated}
          />
        )}
      </div>
      <HamsterFloatingButton />
    </Layout>
  );
};

export default SchernikauGroupDashboard;
